import * as Yup from 'yup';
import React, { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Grid, Hidden } from '@mui/material';
import Banner from '@cpg-its/ads.containers.banner';
import { BasicInfo } from './basic-info';
import { Person } from '../../../../global/models/person-model';
import { PhoneInfo } from './phone-info';
import { EmailInfo } from './email-info';
import { AddressField } from '../../../../global/components/form-element/address';
import { getRequiredText } from '../../../../global/helpers/transformer';
import { FormTypes, MailingAddressFieldNames } from '../../models/form-models';
import { getAddressLabels } from '../../selectors/form-selectors';
import { PersonalFormValues, PersonalInfoMailingAddressRequiredSchema } from './personal-form-models';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { ApiStatuses } from '../../../../global/models/api-model';
import { MaritalInfo } from './marital-info';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { PersonInfoValidationSchema } from '../../../../global/validation-schemas/account-information-form-validation';
import { PersonInfoMailingAddressValidationSchema } from '../../../../global/validation-schemas/address-form-validation';
import { getFormattedDate } from '../../../../global/helpers/date-helpers';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { TcsmStates } from '../../../../root/tcsm-model';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { AnnualEnrollmentProps } from '../../../arc/models/arc-epi-props';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormBaseModel } from '../../../../global/models/page-model';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../helpers/form-init-helpers';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { getCurrentFormattedDateNY } from '../../../../global/helpers/form-data-helper';
import { useSearchParams } from '../../../../global/hooks/useSearchParam';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { NoChangeSuffix } from '../../../../global/components/form-element/form-success-message';
import { QuerySuccessMessage } from '../../../../global/components/messages/query-success-message';
import { PersonalUpdateStatus } from './personal-info-models';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { PersonalInfoProps } from '../../models/account-info-epi-props';


interface PersonUpdateProps extends FormBaseModel {
	onSubmitHandler: (value: PersonalFormValues, initialValues: PersonalFormValues, formikHelpers: FormikHelpers<PersonalFormValues>) => any,
	currentPerson: Person,
	labelSetting: LabelSettingProps,
	isEnrollmentForm: boolean;
	isAnnualEnrollmentForm: boolean;
	gpsType: GpsType;
	hasMaritalStatusSection: boolean;
	submitButtonText: string;
	cancelButtonText: string;
	preventButtonDisableValidation: boolean;
	onCancelHandler: () => any;
	docUpload?: boolean;
}

export function PersonUpdate(props: PersonUpdateProps) {

	const app = useContext(DemographicsContext);
	const success = useSelector<TcsmStates, ApiStatuses>(state => state.personalInfo[app]?.patchPersonStatus);
	const isPersonClergy = useSelector<TcsmStates, string>(state => state?.personalInfo[app]?.currentPerson?.cleric_in_book) === 'Y';
	const apiErrors = useApiResponseError();
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const personalInfoSettings = useSelector<TcsmStates, PersonalInfoProps>(state => state.epiProps?.personalInfoSettingState?.epiContent);
	const { currentPerson, gpsType, isEnrollmentForm, isAnnualEnrollmentForm, hasMaritalStatusSection, submitButtonText, cancelButtonText, preventButtonDisableValidation } = props;
	const gpsItem = getGpsType(gpsType, app);
	const searchParams = useSearchParams();
	const validationSchema = Yup.lazy(({
		date_of_birth,
		business_phone_formated,
		business_phone_country_code,
		mobile_phone_formated,
		mobile_phone_country_code,
		home_phone_formated,
		home_phone_country_code,
	}) => {
		let schema = Yup.object().shape({
			...PersonInfoValidationSchema(props.labelSetting, date_of_birth, {
				business_phone_formated,
				business_phone_country_code,
				mobile_phone_formated,
				mobile_phone_country_code,
				home_phone_formated,
				home_phone_country_code,
			}),
			...PersonInfoMailingAddressValidationSchema(props.labelSetting),
			personal_email_address: Yup.string().email(props.labelSetting.emailFormatError).ensure().when('office_email_address', {
				is: '',
				then: Yup.string().required(props.labelSetting.atLeastOneEmail),
			}),
			office_email_address: Yup.string().email(props.labelSetting.emailFormatError).ensure().when('personal_email_address', {
				is: '',
				then: Yup.string().required(props.labelSetting.atLeastOneEmail),
			}),

		},
			[['office_email_address', 'personal_email_address']]);

		if (isPersonClergy) {
			schema = schema.shape({
				designation_salutation: Yup.string().required(getRequiredText(props.labelSetting.requiredFormat, props.labelSetting.salutationLabel)),
			});
		}

		return schema;
	});

	const initialValues: PersonalFormValues = {

		...getInitialPersonInfo(currentPerson),
		...getInitialMailingAddress(currentPerson),
		...getInitialPhones(currentPerson),
		...getInitialEmails(currentPerson),
	};

	const addressExpired = getFormattedDate(props?.currentPerson?.mailing_address_expiration_date) < getCurrentFormattedDateNY();
	const [addressExpiredError, setAddressExpiredError] = useState(addressExpired);

	const formTypeDefault = app === DemographicAppKeys.arc ? FormTypes.MAP_PERSONAL_INFO : FormTypes.PERSONAL_INFO;
	let formType: any[] = [];

	if (success === ApiStatuses.Success) {
		formType = [searchParams.get('docUpload') ? FormTypes.PERSON_DOC_UPLOAD_SUCCESS : formTypeDefault];
	} else if (success === ApiStatuses.NoChange) {
		formType = [`${formTypeDefault}${NoChangeSuffix}`];
	}

	return (
		<>
			{searchParams?.get('status') === PersonalUpdateStatus.MARTIAL_STATUS &&
				<ContentSpacer>
					<Banner banner={{
						type: 'success',
						label: <WysiwygWithReactLink content={personalInfoSettings.maritalStatusSuccessMessage} />,

					}} />
				</ContentSpacer>}
			{isAnnualEnrollmentForm && app === DemographicAppKeys.arc &&
				<ContentSpacer>
					<div dangerouslySetInnerHTML={{ __html: annualEnrollmentSettings?.adminAccessMessage }} />
				</ContentSpacer>}
			<Formik
				initialValues={initialValues}
				onSubmit={(values, formikHelpers) => props.onSubmitHandler(values, initialValues, formikHelpers)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<PersonalFormValues>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={submitButtonText}
							cancelText={cancelButtonText}
							onCancel={() => {
								formProps.resetForm();
								props.onCancelHandler();
							}}
							successfulForms={formType}
							submitDisabled={addressExpiredError && !isEnrollmentForm}
							preventButtonDisableValidation={preventButtonDisableValidation}
							formType={FormTypes.PERSONAL_INFO}
						>
							<QuerySuccessMessage successfulAction={FormTypes.RETIREE_SUCCESS} />
							<PageHeader
								headerText={props.formHeader}
								description={props.formDescription}
							/>
							<Hidden mdUp>
								<SideBarContent>
									<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
								</SideBarContent>
							</Hidden>
							<Grid>
								<DividerSection >
									<BasicInfo
										labelSetting={props.labelSetting}
										formikProps={formProps}
										salutationRequired={isPersonClergy}
										currentPerson={currentPerson}
										docUpload={props?.docUpload}
									/>
								</DividerSection>
								<DividerSection sectionText={props.labelSetting.mailingAddressTitle} showCleryIcon>
									<AddressField
										currentCountry={formProps.values.mailing_country === '' ? 'US' : formProps.values.mailing_country}
										labels={getAddressLabels(props.labelSetting)}
										addressFieldNames={MailingAddressFieldNames}
										formProps={formProps}
										requiredSchema={PersonalInfoMailingAddressRequiredSchema}
										addressExpired={addressExpired && !isEnrollmentForm}
										addressExpiredError={addressExpiredError && !isEnrollmentForm}
										expiredAddressChangeToggle={(hasChanged: boolean) => setAddressExpiredError(!hasChanged)}
										person
										currentPerson={currentPerson}
										docUpload={props?.docUpload}
									/>

								</DividerSection>
								<PhoneInfo
									labelSetting={props.labelSetting}
									formProps={formProps}
								/>
								<EmailInfo
									labelSetting={props.labelSetting}
									formProps={formProps}

								/>
								{
									hasMaritalStatusSection &&
									<MaritalInfo />
								}
							</Grid>

						</FormWrapper>
					</Form>
				)}
			</Formik>
		</>

	);
}

PersonUpdate.defaultProps = {
    docUpload: true,
};